import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';
import { recency } from './rows';
import { ActionChip } from './pills';
import { UndoToast } from './UndoToast';

/**
 * Dashboard — #62 today_dashboard_1 (canonical variant) + the Dashboard depth spec §5 (cmdr,
 * 2026-08-23): "every widget resolves to a destination" (a number that isn't clickable is a dead
 * end) and "the ONE place the dashboard should ACT, not navigate" — Complete a task inline from Tasks
 * Due Today, same action §1 already ships, same UndoToast.
 *
 * Navigation this pass = switching to the right TAB (a real destination, not a dead end); deep-linking
 * to the specific selected row within that tab is deferred — same "field exists, richer version
 * later" pattern as primaryContactId/dealStalenessThresholdDays this session.
 *
 * Colour is deliberately NOT applied to the stage/task counts: no threshold or direction was
 * specified for them (unlike Pipeline's Last-Activity, which has an explicit, tenant-declared rule).
 */
interface Summary {
  activeLeadsCount: number;
  taskCounts: { backlog: number; in_progress: number; review: number };
  dealsByStage: { id: string; name: string; dealCount: number }[];
  recentActivity: { id: string; type: string; summary: string | null; subjectName: string | null; subjectKind: 'lead' | 'contact' | 'company' | 'deal' | null; subjectId: string | null; occurredAt: string }[];
  tasksDueToday: { id: string; title: string; status: string; dueDate: string | null; completedAt: string | null }[];
  revenueToday: number | null;
}

type NavTarget = 'customers' | 'contacts' | 'accounts' | 'pipeline' | 'tasks';

const NumberTile: React.FC<{ label: string; value: number; onClick?: () => void; testId?: string }> = ({ label, value, onClick, testId }) => {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp type={onClick ? 'button' : undefined} data-testid={testId} onClick={onClick}
      className={`p-3 rounded-xl bg-canvas text-start w-full ${onClick ? 'hover:bg-border-neutral cursor-pointer' : ''}`}>
      <div className="text-2xl font-bold text-text-strong" dir="ltr">{value}</div>
      <div className="text-xs text-text-muted">{label}</div>
    </Comp>
  );
};

export const Dashboard: React.FC<{ onNavigate: (tab: NavTarget) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation('common');
  const [toast, setToast] = useState<{ message: string; onUndo?: () => void } | null>(null);

  // X-1 via useListFetch — not one of P38's ten census surfaces, but the identical copied idiom
  // (failed load -> loading-forever); adopted so the class is CLOSED in this directory, not merely
  // reduced (the A2 done-condition is a zero-grep, and an allowed exception is how a census rots).
  const list = useListFetch<Summary>('/api/dashboard/summary');
  const summary = list.data;
  const setSummary = list.setData as React.Dispatch<React.SetStateAction<Summary | null>>;

  const toggleComplete = async (task: Summary['tasksDueToday'][number]) => {
    if (!summary) return;
    const wasComplete = !!task.completedAt;
    setSummary((s) => s ? { ...s, tasksDueToday: s.tasksDueToday.map((x) => x.id === task.id ? { ...x, completedAt: wasComplete ? null : new Date().toISOString() } : x) } : s); // optimistic
    try {
      const res = await fetch(`/api/tasks/${task.id}/${wasComplete ? 'uncomplete' : 'complete'}`, { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      if (!wasComplete) {
        setToast({ message: t('taskBoard.toast.completed'), onUndo: () => { setToast(null); void toggleComplete({ ...task, completedAt: new Date().toISOString() }); } });
      }
    } catch {
      setSummary((s) => s ? { ...s, tasksDueToday: s.tasksDueToday.map((x) => x.id === task.id ? { ...x, completedAt: task.completedAt } : x) } : s); // revert
      setToast({ message: t('taskBoard.toast.completeFailed') });
    }
  };

  const activityTarget = (kind: Summary['recentActivity'][number]['subjectKind']): NavTarget | null =>
    kind === 'lead' ? 'customers' : kind === 'contact' ? 'contacts' : kind === 'company' ? 'accounts' : kind === 'deal' ? 'pipeline' : null;

  if (list.status === 'error') {
    return <ListStateShell status="error" testIdBase="dashboard" onRetry={list.retry}>{null}</ListStateShell>;
  }
  if (!summary) return <div className="p-6 text-sm text-text-muted">{t('dashboard.loading')}</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 overflow-y-auto">
      <div data-testid="dashboard-tasks" className="bg-surface border border-border-neutral rounded-xl p-4">
        <h3 className="text-label-caps uppercase text-text-muted mb-3">{t('dashboard.tasksFollowUps')}</h3>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <NumberTile label={t('taskBoard.stages.backlog')} value={summary.taskCounts.backlog} />
          <NumberTile label={t('taskBoard.stages.in_progress')} value={summary.taskCounts.in_progress} />
          <NumberTile label={t('taskBoard.stages.review')} value={summary.taskCounts.review} />
        </div>
        {/* Tasks Due Today — the ONE place this screen ACTS, not navigates (§5). */}
        <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('dashboard.tasksDueToday')}</div>
        {summary.tasksDueToday.length === 0 ? (
          <div className="text-sm text-text-muted" data-testid="dashboard-tasks-due-empty">{t('dashboard.tasksDueTodayEmpty')}</div>
        ) : (
          <ul className="space-y-2" data-testid="dashboard-tasks-due-list">
            {summary.tasksDueToday.map((task) => (
              <li key={task.id} className="flex items-center justify-between gap-2">
                <button type="button" data-testid="dashboard-task-title" onClick={() => onNavigate('tasks')}
                  className={`text-sm text-start truncate hover:underline ${task.completedAt ? 'line-through text-text-muted' : 'text-text-strong'}`}>
                  {task.title}
                </button>
                <ActionChip testId="dashboard-task-complete" label={task.completedAt ? t('taskBoard.undo') : t('taskBoard.complete')} onAct={() => toggleComplete(task)} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid="dashboard-recent-activity" className="bg-surface border border-border-neutral rounded-xl p-4">
        <h3 className="text-label-caps uppercase text-text-muted mb-3">{t('dashboard.recentChanges')}</h3>
        {summary.recentActivity.length === 0 ? (
          <div className="text-sm text-text-muted">{t('dashboard.noChangesYet')}</div>
        ) : (
          <ul className="space-y-2">
            {summary.recentActivity.map((a) => {
              const target = activityTarget(a.subjectKind);
              const label = a.subjectName || a.summary || t(`dashboard.activityType.${a.type}`, a.type);
              return (
                <li key={a.id} className="text-sm">
                  {target ? (
                    <button type="button" onClick={() => onNavigate(target)} className="text-text-strong hover:underline text-start">{label}</button>
                  ) : (
                    <span className="text-text-strong">{label}</span>
                  )}
                  <span className="text-text-muted ms-2 text-xs" dir="ltr">{recency(a.occurredAt, t)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div data-testid="dashboard-pipeline-snapshot" className="bg-surface border border-border-neutral rounded-xl p-4">
        <h3 className="text-label-caps uppercase text-text-muted mb-3">{t('dashboard.pipelineSnapshot')}</h3>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <NumberTile label={t('dashboard.activeLeads')} value={summary.activeLeadsCount} onClick={() => onNavigate('customers')} testId="dashboard-active-leads" />
          {summary.dealsByStage.map((s) => (
            <NumberTile key={s.id} label={s.name} value={s.dealCount} onClick={() => onNavigate('pipeline')} />
          ))}
        </div>
        {/* Revenue Today — uncomputable until Deal.status/closedAt landed (Pipeline-complete-loop);
            never $0 when there are none, "$0" is a measurement claim and "none" is a fact. */}
        <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1">{t('dashboard.revenueToday')}</div>
        {summary.revenueToday == null ? (
          <div className="text-sm text-text-muted" data-testid="dashboard-revenue-today-empty">{t('dashboard.revenueTodayEmpty')}</div>
        ) : (
          <div className="text-2xl font-bold text-text-strong" dir="ltr" data-testid="dashboard-revenue-today">{summary.revenueToday.toLocaleString()}</div>
        )}
      </div>

      {toast && <UndoToast message={toast.message} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
    </div>
  );
};
